import { itemForm } from '@/pages/menu-managements/items/service';
import { FormError, validBlank, validMaxStr } from '@/validate';
import { format } from '@/validate/message';

export type ItemFormError = {
  titleJa: FormError;
  memo: FormError;
  limitOfPurchase: FormError;
  description: FormError;
  salesPeriod: FormError;
  takeoutPrice: FormError;
  deliveryPrice: FormError;
  eatInPrice: FormError;
  relativeSalesPeriod: FormError;
  specialInstructionsTitle: FormError;
  specialInstructionsPlaceholder: FormError;
};

export const validateTitleJa = (
  titleJa: string,
  error: ItemFormError
): ItemFormError => {
  error.titleJa.isTouched = true;
  const label = itemForm.titleJa.label;
  const isMax = validMaxStr.check(titleJa, 80);
  const isBlank = validBlank.check(titleJa);
  if (isBlank) error.titleJa.message = validBlank.message(label);
  if (isMax) error.titleJa.message = validMaxStr.message(label, 80);
  error.titleJa.isError = isBlank || isMax;
  return error;
};

export const validateMemo = (
  memo: string,
  error: ItemFormError
): ItemFormError => {
  error.titleJa.isTouched = true;
  const label = itemForm.memo.label;
  const isMax = validMaxStr.check(memo, 2000);
  if (isMax) error.memo.message = validMaxStr.message(label, 2000);
  error.memo.isError = isMax;
  return error;
};

export const validateDescription = (
  description: string,
  error: ItemFormError
): ItemFormError => {
  error.description.isTouched = true;
  const label = itemForm.description.label;
  const isMax = validMaxStr.check(description, 2000);
  if (isMax) error.description.message = validMaxStr.message(label, 2000);
  error.description.isError = isMax;
  return error;
};

export const validateRelativeSalesPeriod = (
  salesRelativeStartAt: number | null,
  salesRelativeEndAt: number | null,
  error: ItemFormError
): ItemFormError => {
  error.relativeSalesPeriod.isTouched = true;
  error.relativeSalesPeriod.isError = false;
  error.relativeSalesPeriod.message = '';
  if (salesRelativeStartAt && salesRelativeEndAt) {
    if (salesRelativeStartAt > salesRelativeEndAt) {
      error.relativeSalesPeriod.message =
        '開始日は終了日よりも前の日付を選択してください';
      error.relativeSalesPeriod.isError = true;
    }
  }
  return error;
};

export const validateSalesPeriod = (
  salesStartAt: Date | null,
  salesEndAt: Date | null,
  error: ItemFormError
): ItemFormError => {
  error.salesPeriod.isTouched = true;
  error.salesPeriod.isError = false;
  error.salesPeriod.message = '';
  if (salesStartAt && salesEndAt) {
    if (salesStartAt >= salesEndAt) {
      error.salesPeriod.message =
        '販売開始日時は販売終了日時より前にしてください';
      error.salesPeriod.isError = true;
    }
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (salesStartAt && salesStartAt < today) {
    error.salesPeriod.message = '販売開始日時は本日以降にしてください';
    error.salesPeriod.isError = true;
  }
  if (salesEndAt && salesEndAt < today) {
    error.salesPeriod.message = '販売終了日時は本日以降にしてください';
    error.salesPeriod.isError = true;
  }
  return error;
};

const validatePrice = (
  price: string,
  error: ItemFormError,
  label: string,
  targetKey: 'takeoutPrice' | 'deliveryPrice' | 'eatInPrice'
): ItemFormError => {
  error[targetKey].isTouched = true;
  const pattern = /^([0-9-]*)$/; // 半角数字とマイナス記号を許容する正規表現
  let isValidFormat = false;
  if (
    !pattern.test(price) ||
    // ２文字目以降にマイナスがあルカをチェック
    (price.length > 1 && price.slice(1).includes('-'))
  ) {
    isValidFormat = true;
  }
  const isBlank = validBlank.check(price);
  if (isBlank) error[targetKey].message = validBlank.message(label);
  if (isValidFormat) error[targetKey].message = format(label);
  const onlyHypen = price === '-';
  if (onlyHypen) error[targetKey].message = format(label);
  error[targetKey].isError = isBlank || isValidFormat || onlyHypen;
  return error;
};

export const validateTakeoutPrice = (
  takeoutPrice: string,
  error: ItemFormError
): ItemFormError => {
  return validatePrice(
    takeoutPrice,
    error,
    itemForm.takeout.price.label,
    'takeoutPrice'
  );
};

export const validateDeliveryPrice = (
  deliveryPrice: string,
  error: ItemFormError
): ItemFormError => {
  return validatePrice(
    deliveryPrice,
    error,
    itemForm.delivery.price.label,
    'deliveryPrice'
  );
};

export const validateEatInPrice = (
  eatInPrice: string,
  error: ItemFormError
): ItemFormError => {
  return validatePrice(
    eatInPrice,
    error,
    itemForm.eatIn.price.label,
    'eatInPrice'
  );
};

export const validateSpecialInstructionsTitle = (
  title: string,
  enabled: boolean,
  error: ItemFormError
): ItemFormError => {
  error.specialInstructionsTitle.isTouched = true;
  error.specialInstructionsTitle.isError = false;
  error.specialInstructionsTitle.message = '';
  if (!enabled) {
    return error;
  }
  const label = '項目名';
  const isBlank = validBlank.check(title);
  const isMax = validMaxStr.check(title, 80);
  if (isBlank)
    error.specialInstructionsTitle.message = validBlank.message(label);
  if (isMax)
    error.specialInstructionsTitle.message = validMaxStr.message(label, 80);
  error.specialInstructionsTitle.isError = isBlank || isMax;
  return error;
};

export const validateSpecialInstructionsPlaceholder = (
  placeholder: string,
  error: ItemFormError
): ItemFormError => {
  error.specialInstructionsPlaceholder.isTouched = true;
  error.specialInstructionsPlaceholder.isError = false;
  error.specialInstructionsPlaceholder.message = '';
  const label = '説明文（入力欄に表示される文言）';
  const isMax = validMaxStr.check(placeholder, 200);
  if (isMax)
    error.specialInstructionsPlaceholder.message = validMaxStr.message(
      label,
      200
    );
  error.specialInstructionsPlaceholder.isError = isMax;
  return error;
};
